import React, { useState, useEffect, useContext, useMemo } from "react";
import { PanierContext } from "../context/PanierContext";
import "../styles/Paiement.css";
import { enregistrerCommande } from "../services/commande";
import { envoyerFacture } from "../services/facture";
import { lancerPaiementOM, verifierStatusPaiement } from "../services/orange";

function Paiement({ showTitle = true }) {
  const { panier, viderPanier } = useContext(PanierContext);

  // utils
  const getPrix = (it) => Number(it.prix ?? it.price ?? 0);
  const getQte = (it) => Number(it.quantite ?? it.quantity ?? 0);

  const total = useMemo(
    () => panier.reduce((t, it) => t + getPrix(it) * getQte(it), 0),
    [panier]
  );

  const [form, setForm] = useState({
    nom: "",
    adresse: "",
    numeroCarte: "",
    expiration: "",
    cvv: "",
  });

  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  // récupération auto client
  useEffect(() => {
    const updateClient = () => {
      const client = JSON.parse(localStorage.getItem("clientConnecte"));
      if (client) {
        setForm({
          nom: `${client.prenom || ""} ${client.nom || ""}`.trim(),
          adresse: client.adresse || "",
          numeroCarte: client.telephone || "",
          expiration: new Date().toISOString().slice(0, 10),
          cvv: "",
        });
      }
    };

    updateClient();
    window.addEventListener("clientUpdated", updateClient);
    return () => window.removeEventListener("clientUpdated", updateClient);
  }, []);

  // Vérification du paiement après retour OM
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const bookingId = params.get("bookingId");

    if (bookingId) checkPaiementOM(bookingId);
  }, []);

  // Vérification Statut Paiement OM
  const checkPaiementOM = async (bookingId) => {
    try {
      setLoading(true);
      const status = await verifierStatusPaiement(bookingId);

      if (!status) {
        setMessage("❌ Erreur de vérification du paiement.");
        return;
      }

      if (status.status === "SUCCESS") {
        finaliserCommande(bookingId);
      } else {
        setMessage("❌ Paiement non confirmé.");
      }
    } catch (e) {
      console.error(e);
      setMessage("❌ Erreur lors du contrôle du paiement.");
    } finally {
      setLoading(false);
    }
  };

  // Finalisation : enregistrement + facture + vider panier
  const finaliserCommande = async (bookingId) => {
    try {
      const client = JSON.parse(localStorage.getItem("clientConnecte")) || {};

      const commande = {
        nom: client.nom,
        prenom: client.prenom,
        email: client.email,
        telephone: client.telephone,
        adresse: form.adresse,
        datePaiement: new Date().toISOString(),
        reference: bookingId,
        montant: total,
      };

      const lignes = panier.map((it) => ({
        id: it.id ?? it.produit_id,
        nom: it.nom ?? it.titre ?? it.description ?? "Produit",
        prix: getPrix(it),
        quantite: getQte(it),
        unite: it.unite ?? it.unit ?? "",
      }));

      await enregistrerCommande(commande, lignes);
      await envoyerFacture(client, commande, lignes);

      viderPanier();
      window.dispatchEvent(new Event("clientUpdated"));

      setDone(true);
      setMessage("✅ Paiement confirmé. Facture envoyée.");
    } catch (e) {
      console.error(e);
      setMessage("❌ Erreur lors de l'enregistrement de la commande.");
    }
  };

  // Lancer paiement OM
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!panier.length || total <= 0) {
      setMessage("❌ Votre panier est vide ou invalide.");
      return;
    }

    try {
      setLoading(true);
      const client = JSON.parse(localStorage.getItem("clientConnecte")) || {};

      const payloadOM = {
        montant: total,
        telephone: client.telephone,
        email: client.email,
        reference: "CMD-" + Date.now(),
      };

      const omRes = await lancerPaiementOM(payloadOM);

      if (!omRes || !omRes.payment_url) {
        setMessage("❌ Impossible de générer le lien de paiement.");
        return;
      }

      // redirection vers page OM
      window.location.href = omRes.payment_url;
    } catch (err) {
      console.error(err);
      setMessage("❌ Une erreur est survenue.");
    } finally {
      setLoading(false);
    }
  };

  const fmt = (n) => Number(n || 0).toLocaleString("fr-FR");

  // --- UI ---
  return (
    <div className={`paiement-container ${done ? "is-success" : ""}`}>
      {message && <div className="message-top">{message}</div>}

      {!done && (
        <>
          <div className="etapes-panier">
            <div className="etape">✔ Vérifier</div>
            <div className="etape">✔ Adresse</div>
            <div className="etape active">Confirmer</div>
          </div>

          {showTitle && <h2>Informations de Paiement</h2>}

          <h3>Détails de la commande</h3>

          {!panier.length ? (
            <p>Votre panier est vide.</p>
          ) : (
            <ul className="liste-produits-commande">
              {panier.map((it) => {
                const prix = getPrix(it);
                const qte = getQte(it);
                return (
                  <li key={it.id ?? it.produit_id}>
                    {it.nom ?? it.titre ?? it.description} — {fmt(qte)}{" "}
                    {it.unite ?? ""} × {fmt(prix)} Ar =
                    <strong> {fmt(prix * qte)} Ar</strong>
                  </li>
                );
              })}
            </ul>
          )}

          <p className="total-commande">
            Total : <strong>{fmt(total)} Ar</strong>
          </p>

          <form onSubmit={handleSubmit} className="form-paiement">
            <input type="text" value={form.nom} readOnly />
            <input type="text" value={form.adresse} readOnly />
            <input type="tel" value={form.numeroCarte} readOnly />
            <input type="text" value={form.expiration} readOnly />

            <button disabled={loading || !panier.length}>
              {loading ? "Paiement..." : "Payer avec Orange Money"}
            </button>
          </form>
        </>
      )}
    </div>
  );
}

export default Paiement;
